// src/utils/writingAnswer.ts
import { cleanTextForTTS } from './textBracketSanitize';

// Sprawdzanie odpowiedzi w grach Napisz (Regular i Easy). Hasło i odpowiedź przechodzą tę samą obróbkę i dopiero wtedy
// są porównywane: wielkość liter, interpunkcja, kreski, liczba spacji i rodzaj apostrofu nie mają znaczenia, skróty
// („I'll”) liczą się jak pełne formy, „sb”, „someone” i „somebody” to to samo, a „a/an/the” i „to” na początku
// można pominąć. Treść nawiasu w haśle można wpisać albo pominąć. Warianty rozdzielone ukośnikiem trzeba wpisać
// wszystkie, ale w dowolnej kolejności; kolejność słów w wariancie dalej się liczy („up give” to nie „give up”).

const INVISIBLE = /[\u{200B}-\u{200D}\u{2060}\u{FEFF}]/gu;
const APOSTROPHES = /[\u{2018}\u{2019}\u{02BC}`\u{00B4}]/gu;
const NOT_WORD = /[^\p{L}\p{M}\p{N}']+/gu;

// Skróty z apostrofem rozwijane do pełnych form. „'s” tylko po zaimkach i kilku słowach pytających — przy
// rzeczowniku to dopełniacz („children's”), a nie „is”
const WHOLE_CONTRACTIONS = new Map([
  ["can't", 'cannot'],
  ["won't", 'will not'],
  ["shan't", 'shall not'],
  ["let's", 'let us'],
]);
const SUFFIX_CONTRACTIONS: [string, string][] = [
  ["n't", ' not'],
  ["'re", ' are'],
  ["'ve", ' have'],
  ["'ll", ' will'],
  ["'m", ' am'],
  ["'d", ' would'],
];
const IS_BEFORE_S = ['he', 'she', 'it', 'that', 'there', 'what', 'who', 'where', 'here', 'how'];

// Zastępniki w zwrotach: „ask sb for sth” = „ask someone for something”
const PLACEHOLDERS = new Map([
  ['somebody', 'sb'],
  ['someone', 'sb'],
  ['smb', 'sb'],
  ['something', 'sth'],
  ['smth', 'sth'],
  ["somebody's", "sb's"],
  ["someone's", "sb's"],
  ["smb's", "sb's"],
  ["one's", "sb's"],
]);

const ARTICLES = ['a', 'an', 'the'];

// Ukośnik bez spacji po obu stronach łączy pojedyncze słowa („look at sb/sth”); znak niżej zastępuje go na czas
// rozdzielania wariantów
const GLUED_SLASH = /(\S)\/(?=\S)/g;
const WORD_ALTERNATIVE = '\u0001';

const PARENTHESES = /(\([^()]*\))/;
// Warianty w haśle rozdziela tylko ukośnik — przecinek bywa częścią hasła („go, went, gone”, „yes, please”).
// Odpowiedź czytana jest na oba sposoby
const SLASH = /\//;
const ANY_SEPARATOR = /[/,;]/;
const MAX_COMBINATIONS = 64;

const prepare = (text: string): string => text
  .normalize('NFC')
  .toLowerCase()
  .replace(INVISIBLE, '')
  .replace(APOSTROPHES, "'");

const expandWord = (word: string): string => {
  const known = WHOLE_CONTRACTIONS.get(word) ?? PLACEHOLDERS.get(word);
  if (known) {
    return known;
  }

  if (word.endsWith("'s") && IS_BEFORE_S.includes(word.slice(0, -2))) {
    return `${word.slice(0, -2)} is`;
  }

  const suffix = SUFFIX_CONTRACTIONS.find(([ending]) => word.length > ending.length && word.endsWith(ending));

  return suffix ? `${word.slice(0, -suffix[0].length)}${suffix[1]}` : word;
};

// Pierwsze słowo odpada, jeśli jest na liście i coś po nim zostaje — samo „a” dalej jest hasłem „a”
const dropFirst = (words: string[], droppable: string[]): string[] => (
  words.length > 1 && droppable.includes(words[0]) ? words.slice(1) : words
);

// Jeden wariant jako ciąg słów bez interpunkcji, z rozwiniętymi skrótami i zastępnikami. Apostrof zostaje do
// rozwinięcia skrótów, potem znika („childrens” przejdzie za „children's”)
const normalizeVariant = (variant: string): string => {
  const words = variant
    .replace(NOT_WORD, ' ')
    .split(' ')
    .map((token) => token.replace(/^'+|'+$/g, ''))
    .filter(Boolean)
    .flatMap((token) => expandWord(token).split(' '))
    .map((word) => word.replace(/'/g, ''))
    .filter(Boolean);

  return dropFirst(dropFirst(words, ['to']), ARTICLES).join(' ').replace(/(^| )can not(?= |$)/g, '$1cannot');
};

// Wszystkie połączenia wyborów po kolei: [['look'], ['sb', 'sth']] → ['look sb', 'look sth'].
// null, gdy wyszłoby ich zbyt wiele
const combinations = (choices: string[][], joiner: string): string[] | null => {
  const count = choices.reduce((total, options) => total * options.length, 1);
  if (count > MAX_COMBINATIONS) {
    return null;
  }

  return choices.reduce<string[]>(
    (acc, options) => acc.flatMap((prefix) => options.map((option) => `${prefix}${joiner}${option}`)),
    [''],
  );
};

const expandWordAlternatives = (variant: string): string[] => combinations(
  variant.split(/\s+/).map((token) => token.split(WORD_ALTERNATIVE)),
  ' ',
) ?? [variant.split(WORD_ALTERNATIVE).join(' ')];

// Treść nawiasu jest nieobowiązkowa: „be interested in (music)” daje wersję z „music” i bez. Przy bardzo wielu
// nawiasach zostaje tylko wersja bez nich, ta sama co w podpowiedzi
const parenthesesVersions = (term: string): string[] => combinations(
  term.split(PARENTHESES).map((part, i) => (i % 2 ? ['', part.slice(1, -1)] : [part])),
  '',
) ?? [cleanTextForTTS(term)];

const toVariantSet = (variants: string[]): Set<string> => new Set(variants.map(normalizeVariant).filter(Boolean));

// Trzy odczyty tego samego tekstu: całość jako jeden wariant (separatory jak spacje), warianty rozdzielone każdym
// separatorem i warianty, w których ukośnik bez spacji dzieli tylko pojedyncze słowa
const readings = (text: string, separators: RegExp): Set<string>[] => [
  toVariantSet([text]),
  toVariantSet(text.split(separators)),
  toVariantSet(text.replace(GLUED_SLASH, `$1${WORD_ALTERNATIVE}`).split(separators).flatMap(expandWordAlternatives)),
];

const sameVariants = (a: Set<string>, b: Set<string>): boolean => (
  a.size === b.size && Array.from(a).every((variant) => b.has(variant))
);

// Dawna reguła zostaje jako furtka: co przechodziło dotąd, przechodzi dalej (np. „don't” przy haśle wpisanym „dont”)
const passesOldRule = (answer: string, term: string): boolean => (
  answer.toLowerCase().replace(/[<>|:;'"`=?,.\\/]+/g, '').trim() === cleanTextForTTS(term).toLowerCase().trim()
);

// term — hasło dosłownie, z nawiasami. Uczeń może rozdzielić warianty ukośnikiem, przecinkiem albo średnikiem
export const isAnswerCorrect = (answer: string, term: string): boolean => {
  const preparedAnswer = prepare(answer);
  const answerReadings = [...readings(preparedAnswer, SLASH), ...readings(preparedAnswer, ANY_SEPARATOR)];
  const termReadings = parenthesesVersions(prepare(term)).flatMap((version) => readings(version, SLASH));

  return passesOldRule(answer, term) || answerReadings.some((given) => (
    given.size > 0 && termReadings.some((expected) => sameVariants(given, expected))
  ));
};
